import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Mail, MessageCircle, FileText, ArrowRight } from "lucide-react";

export default function CheckoutSuccess() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");

    useEffect(() => {
        // Aqui você pode fazer uma chamada para confirmar o pagamento
        if (sessionId) {
            console.log("Payment successful, session:", sessionId);
            // Opcional: chamar API para confirmar e iniciar análise
        }
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
            </div>

            <Card className="max-w-2xl w-full border-primary/20 relative z-10">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <CheckCircle2 className="h-10 w-10 text-primary" />
                    </div>
                    <CardTitle className="text-3xl md:text-4xl">Pagamento Confirmado! 🎉</CardTitle>
                    <CardDescription className="text-lg mt-4">
                        Sua análise já está sendo processada
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                        <h3 className="font-semibold text-lg mb-4 text-center">O que acontece agora?</h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary font-bold">1</span>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Análise Automática</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Nosso sistema já iniciou a análise completa do seu site usando as melhores ferramentas do mercado.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary font-bold">2</span>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Processamento</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Estamos analisando performance, SEO, AEO, acessibilidade e melhores práticas.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary font-bold">3</span>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Entrega</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Você receberá o relatório completo por email em até 48 horas.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                            <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                            <h4 className="font-medium text-sm mb-1">Email</h4>
                            <p className="text-xs text-muted-foreground">
                                Relatório completo em PDF
                            </p>
                        </div>

                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                            <MessageCircle className="h-8 w-8 text-primary mx-auto mb-2" />
                            <h4 className="font-medium text-sm mb-1">WhatsApp</h4>
                            <p className="text-xs text-muted-foreground">
                                Notificação quando estiver pronto
                            </p>
                        </div>

                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                            <FileText className="h-8 w-8 text-primary mx-auto mb-2" />
                            <h4 className="font-medium text-sm mb-1">Documentação</h4>
                            <p className="text-xs text-muted-foreground">
                                Guias e recomendações
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6 text-center">
                        <h3 className="font-semibold mb-2">Quer que implementemos as melhorias?</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            O valor investido na análise é 100% descontado se você contratar nossos serviços de desenvolvimento.
                        </p>
                        <Button asChild>
                            <Link to="/orcamento" className="flex items-center gap-2">
                                Solicitar Proposta <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button asChild variant="outline">
                            <Link to="/">Voltar para Home</Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/diagnostico-completo">Ver Outros Planos</Link>
                        </Button>
                    </div>

                    <p className="text-xs text-center text-muted-foreground">
                        Dúvidas? Entre em contato: <a href="mailto:contato@coddar.com.br" className="text-primary hover:underline">contato@coddar.com.br</a>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
